import { cn } from '@/@core/utils/cn'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { fadeIn, staggerContainer } from '@/v2/constants/motion'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import React from 'react'

interface HeroSectionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: string
  description?: string
  image: { url: string; alt: string }
  ctaButton: { href: string; text: string }
}

const HeroSection: React.FC<HeroSectionProps> = props => {
  const { title, description, image, ctaButton, ...rest } = props

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        variants={staggerContainer()}
        initial='hidden'
        animate='show'
        viewport={{ once: true, amount: 0.25 }}
        className='section-container bg-secondary-foreground section-spacing'
      >
        <SectionWrapper {...rest}>
          <div className={'flex items-center justify-between flex-col-reverse md-mn:flex-row gap-y-6 md-mn:gap-3'}>
            <m.div
              variants={fadeIn('right', 'tween', 0, 1)}
              className='flex flex-col items-start justify-center md-mn:max-w-[635px] w-full flex-1 [&>h1]:text-heading-home [&>h1]:md-mn:text-left [&>h1]:text-center'
            >
              <h1 dangerouslySetInnerHTML={{ __html: title }} />

              {description && (
                <p
                  className={cn(
                    'text-paragraph lg-mn:text-[20px] text-base xl-min:mt-[36px] mt-[24px] font-[400] leading-[30px] text-center md-mn:text-left mx-auto'
                  )}
                >
                  {description}
                </p>
              )}

              <LinkWithTransition href={ctaButton.href} className='self-center md-mn:self-start mt-[36px]'>
                <Button
                  variant='call_to_action'
                  className='transition-all animate-bounce xl-min:w-[231px] xl-min:h-[52px] w-[173px] h-[44px]'
                >
                  {ctaButton.text}
                </Button>
              </LinkWithTransition>
            </m.div>

            <m.div variants={fadeIn('left', 'tween', 0, 1)} className=''>
              <ResponsiveImage
                priority
                src={image.url}
                alt={image.alt}
                className='w-[336px] h-[204px] xl:!w-[772px] xl:!h-[468px]'
              />
            </m.div>
          </div>
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default HeroSection
